// Meta Conversions API (CAPI) client-side helper
// Sends events to both the browser pixel (fbq) AND the server-side CAPI
// endpoint with a shared event_id for deduplication.

const META_CAPI_ENDPOINT =
  'https://us-central1-learning-app-6f8ff.cloudfunctions.net/metaConversionsEvent';

/** Generate a unique event ID for deduplication between pixel + CAPI */
function generateEventId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Fire a conversion event to both fbq (browser pixel) and the Conversions API.
 *
 * @param {string}  eventName       – Meta standard event name
 * @param {object}  opts
 * @param {string}  opts.eventId    – optional custom event ID (auto-generated if omitted)
 * @param {object}  opts.userData   – { em, fn, ln, external_id } (raw, unhashed — server hashes)
 * @param {object}  opts.customData – { currency, value, content_name, ... }
 * @param {string}  opts.sourceUrl  – event_source_url override
 */
export function sendConversionEvent(eventName, opts = {}) {
  const eventId = opts.eventId || generateEventId(eventName.toLowerCase());

  // 1) Browser pixel — include eventID for dedup
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      const pixelParams = { ...(opts.customData || {}) };
      // fbq standard events vs custom events
      const standardEvents = [
        'Purchase', 'ViewContent', 'Contact', 'InitiateCheckout',
        'CompleteRegistration', 'StartTrial', 'Subscribe',
      ];
      if (standardEvents.includes(eventName)) {
        window.fbq('track', eventName, pixelParams, { eventID: eventId });
      } else {
        window.fbq('trackCustom', eventName, pixelParams, { eventID: eventId });
      }
    }
  } catch (err) {
    console.warn('[Meta CAPI] fbq call failed:', err);
  }

  // 2) Server-side CAPI — fire and forget
  try {
    fetch(META_CAPI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: opts.sourceUrl || window.location.href,
        user_data: {
          ...(opts.userData || {}),
          client_user_agent: navigator.userAgent,
        },
        custom_data: opts.customData || {},
      }),
      keepalive: true,
    }).catch((err) => console.warn('[Meta CAPI] server call failed:', err));
  } catch (err) {
    console.warn('[Meta CAPI] fetch failed:', err);
  }
}

// ── Convenience wrappers matching the 6 required events ──────────────────

export function trackPurchase(userData, planType) {
  const value = planType === 'annual' ? 107.88 : 10.99;
  sendConversionEvent('Purchase', {
    userData,
    customData: { currency: 'USD', value },
  });
}

export function trackStartTrial(userData) {
  sendConversionEvent('StartTrial', { userData });
}

export function trackViewContent(userData, contentName) {
  sendConversionEvent('ViewContent', {
    userData,
    customData: contentName ? { content_name: contentName } : {},
  });
}

export function trackContact(userData) {
  sendConversionEvent('Contact', { userData });
}

export function trackInitiateCheckout(userData) {
  sendConversionEvent('InitiateCheckout', { userData });
}

export function trackCompleteRegistration(userData) {
  sendConversionEvent('CompleteRegistration', { userData });
}
